import { Card, FormSection } from "../../components/ui";
import { useBranding } from "../../context/BrandingContext";
import { useAuth } from "../../context/AuthContext";

/**
 * Security posture, stated rather than configured.
 *
 * Every value here is a REAL implemented control, read from the code that
 * enforces it — not a toggle that pretends to change something. LaundryPro's
 * session, lockout and CSRF behaviour is deliberately not owner-tunable:
 * letting an operator weaken a brute-force limit or extend a session lifetime
 * from a settings screen is a way to turn a security control into a liability.
 * Per-user session revocation lives on the Users screen, where the account
 * being revoked is in front of you.
 */
export function SecuritySettingsSection() {
  const { language } = useBranding();
  const { user } = useAuth();
  const ar = language === "ar";

  const rows: ReadonlyArray<[string, string, string]> = [
    [ar ? "تخزين كلمة المرور" : "Password storage", "PBKDF2-HMAC-SHA256", ar ? "١٠٠٬٠٠٠ دورة، ملح ١٦ بايت لكل كلمة مرور" : "100,000 iterations, 16-byte per-password salt"],
    [ar ? "عمر الجلسة" : "Session lifetime", ar ? "١٢ ساعة" : "12 hours", ar ? "مع انتهاء بالخمول بعد ١٢٠ دقيقة" : "with a 120-minute idle timeout"],
    [ar ? "ملف الجلسة" : "Session cookie", "HttpOnly · Secure · SameSite=Lax", ar ? "يُخزَّن تجزئة الرمز فقط" : "only the token hash is stored"],
    [ar ? "حماية CSRF" : "CSRF protection", ar ? "رمز مزدوج الإرسال" : "Double-submit token", ar ? "مطلوب على كل طلب يغيّر البيانات" : "required on every state-changing request"],
    [ar ? "قفل المحاولات" : "Brute-force lockout", ar ? "٥ محاولات / ١٥ دقيقة للحساب" : "5 attempts / 15 min per account", ar ? "و٢٠ محاولة / ١٥ دقيقة لكل عنوان IP" : "and 20 / 15 min per IP"],
  ];

  return (
    <div className="space-y-4">
      <Card>
        <FormSection legend={ar ? "الجلسة الحالية" : "Current session"}>
          <div className="text-sm text-ink-900">{user?.fullName}</div>
          <div className="text-xs text-muted-foreground">
            {user?.username}
            {user?.roles?.length ? ` · ${user.roles.map((r) => (ar ? r.nameAr : r.nameEn)).join("، ")}` : ""}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {ar
              ? "لإنهاء جلسات مستخدم آخر، افتح شاشة المستخدمين واستخدم «إنهاء الجلسات»."
              : "To revoke another user's sessions, use “Force logout” on the Users screen."}
          </p>
        </FormSection>
      </Card>

      <Card>
        <FormSection legend={ar ? "الضوابط المطبَّقة" : "Enforced controls"}>
          <dl className="divide-y divide-line-hairline">
            {rows.map(([label, value, hint]) => (
              <div key={label} className="flex flex-wrap items-baseline justify-between gap-2 py-2.5">
                <dt className="text-sm text-ink-700">{label}</dt>
                <dd className="text-end">
                  <div className="text-sm font-medium text-ink-900">{value}</div>
                  <div className="text-xs text-muted-foreground">{hint}</div>
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            {ar
              ? "هذه الضوابط ثابتة عمداً — لا يمكن إضعافها من الإعدادات."
              : "These are deliberately not configurable — they cannot be weakened from a settings screen."}
          </p>
        </FormSection>
      </Card>
    </div>
  );
}
